import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components';
import { IGenericModal, Modal } from 'components/Modal/Modal';
import { H2 } from 'components/Typography/Typography';
import { Button } from 'components/_form';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDataControl } from '../CreateAssessmentModal';
import { IOption } from 'components/_form/Select/Select';
import { Dispatch, SetStateAction, useState } from 'react';
import { IDetailedAssessment } from 'types/assessments';
import {
  closeAssessmentClosing,
  openAssessmentClosing
} from 'api/assessmentsClosings';
import { IAssessmentsClosing } from 'types/assessmentsClosings';
import { isClosingActionError, showRequestError } from 'utilities/showRequestError';
import { Loader } from 'components/Loader/Loader';
import { ClosingTable } from 'components/ClosingTable/ClosingTable';
import { toast } from 'react-toastify';

interface Props extends IGenericModal {
  watch: UseFormWatch<FormDataControl>;
  setValue: UseFormSetValue<FormDataControl>;
  usersList: IOption[];
  userFunctionsList: IOption[];
  selectedAssessment?: IDetailedAssessment;
  setSelectedAssessment?: Dispatch<
    SetStateAction<IDetailedAssessment | undefined>
  >;
}

export const AssessmentStatusModal = ({
  isOpen,
  onCancelClick,
  watch,
  setValue,
  usersList,
  userFunctionsList,
  selectedAssessment,
  setSelectedAssessment
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [isAddingRowVisible, setIsAddingRowVisible] = useState(false);

  const { t } = useTranslation();

  const closingIds = watch('closing_ids') || [];

  const insertAssessmentClosingIntoAssessment = (
    newAssessmentClosing: IAssessmentsClosing
  ) => {
    const assessmentClosing = selectedAssessment?.assessments_closings.find(
      ({ id: findId }) =>
        findId.toString() === newAssessmentClosing.id.toString()
    );

    if (assessmentClosing) {
      setSelectedAssessment?.(
        (prevAssessment) =>
          prevAssessment && {
            ...prevAssessment,
            assessments_closings: assessmentClosing
              ? prevAssessment.assessments_closings.map((assessmentClosing) =>
                  assessmentClosing.id.toString() ===
                  newAssessmentClosing.id.toString()
                    ? newAssessmentClosing
                    : assessmentClosing
                )
              : [...prevAssessment.assessments_closings, newAssessmentClosing]
          }
      );
    }
  };

  const handleCloseAssessment = async (closingId?: string | number) => {
    if (!closingId) return;

    setLoading(true);

    try {
      const closeAssessmentClosingRes = await closeAssessmentClosing(closingId);

      if (isClosingActionError(closeAssessmentClosingRes)) {
        showRequestError(closeAssessmentClosingRes.error, t);
        return;
      }

      if (closeAssessmentClosingRes.data) {
        insertAssessmentClosingIntoAssessment(closeAssessmentClosingRes.data);
        toast.success(t('common.success'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssessment = async (closingId?: string | number) => {
    if (!closingId) return;

    setLoading(true);

    try {
      const openAssessmentClosingRes = await openAssessmentClosing(closingId);

      if (isClosingActionError(openAssessmentClosingRes)) {
        showRequestError(openAssessmentClosingRes.error, t);
        return;
      }

      if (openAssessmentClosingRes.data) {
        insertAssessmentClosingIntoAssessment(openAssessmentClosingRes.data);
        toast.success(t('common.success'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      mainButton={{
        action: onCancelClick,
        variant: 'eucalyptus',
        label: loading ? <Loader /> : t(`buttons.saveAndClose`)
      }}
      gridTemplateColumns="50%"
      bodyMaxHeight="70vh"
      headerTitle={
        <Flex width="100%">
          <H2 variant="h3" mr={2}>
            {t(`assessmentsView.assessmentStatusModal.closingAssessment`)}
          </H2>
        </Flex>
      }
    >
      <Flex flexDirection="column">
        <Flex width="100%" justifyContent="flex-end">
          <Button
            variant="eucalyptus"
            onClick={() => {
              setIsAddingRowVisible(true);
            }}
          >
            <FontAwesomeIcon icon={faPlusCircle} /> {t('buttons.addPerson')}
          </Button>
        </Flex>
        <ClosingTable
          closingIds={closingIds}
          setClosingIds={(ids) => setValue('closing_ids', ids)}
          closings={selectedAssessment?.assessments_closings || []}
          authorId={watch('author_id')}
          leaderId={watch('leader_id')}
          teamLeaderId={watch('team_leader_id')}
          teamManagerId={watch('team_manager_id')}
          usersList={usersList}
          userFunctionsList={userFunctionsList}
          participationsAttributes={
            watch('assessment_participations_attributes') || []
          }
          handleClose={handleCloseAssessment}
          handleOpen={handleOpenAssessment}
          isAddingRowVisible={isAddingRowVisible}
          setIsAddingRowVisible={setIsAddingRowVisible}
        />
      </Flex>
    </Modal>
  );
};
